import React from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, Text } from "@jsq/ds/core";
import { nodeEdgeOffset, TreeSection, NodeWrapper, nodeMaxWidth } from "./style_vanilla.css";
import { TreeConfig, Node } from "./types";
import { validatePosition } from "./util";

/**
 * TreeNode component renders a tree structure using the provided tree nodes and configuration.
 *
 * @param {Array<Node>} treeNodes - An array of tree nodes to be rendered.
 * @param {TreeConfig} treeConfig - Configuration object for the tree.
 * @param {number} level - The current level of the tree, used for indentation and styling.
 *
 * @returns {JSX.Element} The rendered tree view component.
 *
 * @example
 * ```tsx
 * const treeNodes = [
 *   { id: '1', content: 'Node 1', leafNodes: [{ id: '1.1', content: 'Node 1.1' }] },
 *   { id: '2', content: 'Node 2' }
 * ];
 * const treeConfig = { edgeStart: 10, edgeWidth: 20 };
 *
 * <TreeNode treeNodes={treeNodes} treeConfig={treeConfig} level={0} />
 * ```
 */
export const TreeNode: React.FC<{
  treeNodes: Array<Node>;
  treeConfig: TreeConfig;
  level: number;
}> = ({ treeNodes, treeConfig, level }) => {
  const configs = validatePosition(treeConfig);
  const offset = configs.edgeStart + configs.edgeWidth;
  return (
    <div role="tree">
      {treeNodes.map((node) => {
        return (
          <div
            key={`${node.id}`}
            role="treeitem"
            aria-level={level + 1}
            aria-expanded={Boolean(node.leafNodes?.length)}
            aria-selected="false"
          >
            <Flex
              id={`elem-${node.id}`}
              data-testid={`elem-${node.id}`}
              data-level={level}
              className={TreeSection}
              style={assignInlineVars({
                [nodeEdgeOffset]: `${offset * level}px`,
                [nodeMaxWidth]: `${configs.nodeMaxWidth - offset * (level > 2 ? 2 : level)}px`,
              })}
            >
              {typeof node.content === "string" ? (
                <Flex className={NodeWrapper} data-testid={`node-content-${node.id}`}>
                  <Text variant="strong-lg">{node.content}</Text>
                </Flex>
              ) : (
                node.content
              )}
            </Flex>
            {node.leafNodes && (
              <TreeNode treeNodes={node.leafNodes} level={level + 1} treeConfig={treeConfig} />
            )}
          </div>
        );
      })}
    </div>
  );
};
